import { adviserFactsheetOf } from '../../../utils/client-factsheet';
import type { ClientFactsheet, QueueClient } from '../../../types/client';

/**
 * How far a client is from carrying a C.P.I. band, and the cheapest way to
 * close that distance.
 *
 * MIRROR — keep in sync with `server/utils/client.js` `getClientCPI` (the
 * `totalWeight` half of it) and `isClientCPIValid`. The server decides
 * `validScore`; this decides what to ASK FOR when it comes back false, which
 * the server never computes and the payload therefore never carries.
 *
 * The point of it: the C.P.I. is a dead loop from the adviser's side — no data,
 * no analysis, and no reason to enter data because there is no analysis. What
 * breaks the loop is that the loop is much shorter than it looks. The threshold
 * is 2.5 of a possible 4.2, two axes have a floor that every client clears for
 * free, and one date picker is worth 1.8 of the rest. Most clients are one or
 * two fields away from a score; nothing in the UI has ever said which two.
 */

/** `server/constants/client.js` CPI_LEAST_VALID_WEIGHT. Strictly greater-than. */
export const CPI_WEIGHT_THRESHOLD = 2.5;

/**
 * Weight that costs nothing: 家庭 scores 0.2 with no family and 職業 scores 0.1
 * with no occupation, so neither axis is ever 0 and both weights always count.
 */
export const CPI_FREE_WEIGHT = 0.3;

export const CPI_FIELDS = ['personality', 'marriageStatus', 'child', 'dob', 'monthlyIncome'] as const;
export type CpiField = (typeof CPI_FIELDS)[number];

/**
 * Input cost, cheapest first — and `CPI_FIELDS` is declared in this order, so a
 * plan of equal cost keeps it.
 *
 * This is not the same ranking as value. 生日 is worth 1.8 and DISC 0.4, but
 * DISC is one tap on a four-way picker the drawer already shows, while a date
 * of birth is a date picker and a fact the adviser may simply not have to hand.
 * 月入 is last by a distance: it is one number, but it is the one question an
 * adviser cannot ask casually, so a prompt that leads with it reads as a
 * demand rather than as a shortcut.
 */
const FIELD_COST: Record<CpiField, number> = {
  personality: 1,
  marriageStatus: 1,
  child: 2,
  dob: 3,
  monthlyIncome: 5
};

type AxisState = {
  /** Weight this axis contributes once it scores. */
  weight: number;
  /** Already scoring. */
  filled: boolean;
  /** Fields that would make it score; empty means nothing can (see `usable`). */
  needs: CpiField[];
  /**
   * False when the axis cannot be made to score at all right now — DISC for an
   * adviser who has not set their own. Filling `needs` would buy nothing, so no
   * plan may include it.
   */
  usable: boolean;
};

export type CpiGapInput = {
  adviserEmail?: string;
  /** The adviser's own DISC letter. Without it, a client's DISC scores 0. */
  adviserPersonality?: string | null;
  /**
   * Answers the adviser has just given that the store has not caught up with
   * yet, laid over the stored factsheet.
   *
   * A write costs three round trips before the store can answer — the
   * `updateFactSheet` itself, then the GET_CLIENT_BY_ID and GET_USER its epic
   * chains after a successful write (store/client/duck.js). Without this, the
   * countdown and the tile that opened the popup would both keep reporting the
   * question the adviser has already answered for as long as all three take.
   *
   * Typed loosely on purpose: the caller's echo covers more of the factsheet
   * than the C.P.I. scores (性別, 類型, 潛在招募 — the last a boolean). Only the
   * axes below are read, so the extra keys ride along harmlessly rather than
   * forcing the drawer to narrow its own state before every call.
   */
  overrides?: Partial<Record<string, string | number | boolean | null>>;
};

export type CpiGap = {
  /** Weight already earned, mirroring the server's `totalWeight`. */
  weight: number;
  /** Weight still needed. 0 once the client is over the line. */
  gap: number;
  /** The server's `validScore`, recomputed — true means a band is already due. */
  unlocked: boolean;
  /**
   * Whether a band is possible at all, mirroring `isClientCPIValid`'s
   * relationship gate. False for a 已中斷 client, whatever is on file.
   *
   * Separate from `unlocked` because an empty `plan` means both "already
   * ranked" and "can never be ranked", and a caller deciding whether to offer
   * the adviser anything needs to tell those apart.
   */
  rankable: boolean;
  /**
   * The cheapest set of empty fields that would cross the threshold, in the
   * order to ask for them. Empty when already unlocked, and empty when no set
   * can get there (which happens: a disconnected client with no dob has only
   * 1.2 of reachable weight left).
   */
  plan: CpiField[];
  /** `plan[0]` — the single field to put in front of the adviser. */
  next: CpiField | null;
};

const isFilled = (value: unknown): boolean => value !== null && value !== undefined && value !== '';

/**
 * Whether this client can carry a band at all, mirroring `isClientCPIValid`'s
 * relationship gate. A disconnected client has withdrawn the policy data half
 * the score is computed from, so they are excluded from the ranking outright —
 * no amount of factsheet filling changes that, and asking for a field that
 * cannot pay off is the one prompt this feature must never show.
 *
 * Read off the two fields the payload carries, exactly as `formatClientData`
 * computes its own gate, rather than re-derived from the connection pill (which
 * reports `passwordChangeKey` as Pending and would disagree).
 */
const isRankable = (client: QueueClient): boolean =>
  Boolean(client.inactiveUser) || (client as { connect?: boolean }).connect === true;

const axesOf = (client: QueueClient, input: CpiGapInput): AxisState[] => {
  // In-flight answers win over the stored ones. `age` is deliberately NOT
  // overridden alongside `dob`: the axis below already treats a dob as paying
  // for both, so a just-typed birthday scores its full 1.8 without this needing
  // to know how the server derives the age.
  const factsheet = {
    ...(adviserFactsheetOf(client, input.adviserEmail) || {}),
    ...(input.overrides || {})
  } as ClientFactsheet;
  const hasDob = isFilled(factsheet.dob);
  const hasIncome = isFilled(factsheet.monthlyIncome);
  const adviserDisc = isFilled(input.adviserPersonality);

  return [
    { weight: 1, filled: hasDob, needs: ['dob'], usable: true },
    // Age is derived from dob (server utils/date.js getAgeFromDob), so a dob
    // pays for this axis as well — which is what makes one date picker the
    // single most valuable keystroke in the whole factsheet.
    { weight: 0.8, filled: hasDob || isFilled(factsheet.age), needs: ['dob'], usable: true },
    { weight: 0.6, filled: hasIncome, needs: ['monthlyIncome'], usable: true },
    { weight: 0.3, filled: hasIncome, needs: ['monthlyIncome'], usable: true },
    { weight: 0.4, filled: isFilled(factsheet.marriageStatus), needs: ['marriageStatus'], usable: true },
    { weight: 0.4, filled: isFilled(factsheet.child), needs: ['child'], usable: true },
    {
      weight: 0.4,
      filled: adviserDisc && isFilled(factsheet.personality),
      needs: ['personality'],
      usable: adviserDisc
    }
  ];
};

/** Weight the axes would reach with `fields` filled on top of what is there. */
const weightWith = (axes: AxisState[], fields: Set<CpiField>): number =>
  axes.reduce(
    (total, axis) =>
      axis.filled || (axis.usable && axis.needs.some(need => fields.has(need))) ? total + axis.weight : total,
    CPI_FREE_WEIGHT
  );

/**
 * Cheapest subset of the still-empty fields that crosses the threshold.
 *
 * Brute force over the subsets, because greedy gets this wrong in the case that
 * matters most: by cost it would reach for DISC, 婚姻 and 子女 (three taps,
 * 1.2 of weight) where 生日 alone is worth 1.8 and finishes the job in one.
 * Five fields is 32 subsets — the whole search is cheaper than the memo that
 * would protect it.
 */
const cheapestPlan = (axes: AxisState[], candidates: CpiField[]): CpiField[] => {
  let best: CpiField[] | null = null;
  let bestCost = Infinity;

  for (let mask = 1; mask < 1 << candidates.length; mask += 1) {
    const subset = candidates.filter((_field, index) => mask & (1 << index));
    const cost = subset.reduce((total, field) => total + FIELD_COST[field], 0);
    // `>=` on the cost keeps the FIRST subset found at a tie, and the loop walks
    // masks in ascending order, so ties go to the fewer/earlier-listed fields.
    if (cost >= bestCost) continue;
    if (weightWith(axes, new Set(subset)) <= CPI_WEIGHT_THRESHOLD) continue;
    best = subset;
    bestCost = cost;
  }

  return best || [];
};

export const cpiGapOf = (client: QueueClient, input: CpiGapInput = {}): CpiGap => {
  const axes = axesOf(client, input);
  const weight = weightWith(axes, new Set());
  const rankable = isRankable(client);
  const unlocked = weight > CPI_WEIGHT_THRESHOLD && rankable;
  // Already ranked, or excluded from the ranking entirely — either way there is
  // nothing to ask for. `unlocked` separates the two for the caller; the empty
  // plan is what both have in common.
  if (unlocked || !rankable) {
    return { weight, gap: 0, unlocked, rankable, plan: [], next: null };
  }

  const candidates = CPI_FIELDS.filter(field =>
    axes.some(axis => !axis.filled && axis.usable && axis.needs.includes(field))
  );
  const plan = cheapestPlan(axes, candidates);

  return {
    // Float noise: the weights are tenths, and summing them in JS produces
    // 2.0999999999999996 often enough to make a "差 0.4" read as "差 0.4000001".
    weight: Math.round(weight * 10) / 10,
    gap: Math.round((CPI_WEIGHT_THRESHOLD - weight) * 10) / 10,
    unlocked,
    rankable,
    plan,
    next: plan[0] || null
  };
};

/**
 * Clients one single field away from a score — the list worth putting in front
 * of an adviser, because every entry is one tap or one date from paying off.
 *
 * Deliberately `plan.length === 1` and not "closest first": a client needing two
 * fields is a different kind of task, and mixing them turns a two-minute pass
 * through the list into an open-ended one.
 */
export const quickUnlockableOf = (clients: QueueClient[], input: CpiGapInput = {}): QueueClient[] =>
  (clients || []).filter(client => cpiGapOf(client, input).plan.length === 1);
